/**
 * Configuration › Appareils : le gérant choisit, appareil par appareil, « Appli complète »
 * ou « PDA stock ». Le changement s'applique sur l'appareil en moins d'une minute.
 */
import { Loader2, Smartphone } from 'lucide-react';
import { toast } from 'sonner';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { errMsg } from '@/api/errors';
import {
  listTenantDevices,
  setDeviceRole,
  type DeviceRole,
  type TenantDevice,
} from '@/api/devices/deviceRoleApi';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { getOrCreateDeviceId } from '@/hooks/useDeviceHeartbeat';
import { useEffectiveTenantId } from '@/hooks/useTenant';

const ROLES: readonly { value: DeviceRole; label: string }[] = [
  { value: 'standard', label: 'Appli complète' },
  { value: 'pda_stock', label: 'PDA stock' },
];

const lastSeen = (iso: string | null): string =>
  iso
    ? new Date(iso).toLocaleString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })
    : 'jamais';

function DeviceCard({
  device,
  current,
  saving,
  onRole,
}: {
  device: TenantDevice;
  current: boolean;
  saving: boolean;
  onRole: (role: DeviceRole) => void;
}) {
  return (
    <li className="space-y-3 rounded-lg border border-border p-3" data-testid="device-card">
      <div className="flex items-start gap-3">
        <Smartphone className="mt-1 h-5 w-5 shrink-0 text-muted-foreground" />
        <div className="min-w-0 flex-1">
          <p className="flex flex-wrap items-center gap-2 font-semibold">
            <span className="break-all">{device.device_name || device.model || 'Appareil'}</span>
            {current && <Badge>Cet appareil</Badge>}
          </p>
          <p className="text-sm text-muted-foreground">
            Vu le {lastSeen(device.last_heartbeat_at)}
            {device.app_version ? ` · v${device.app_version}` : ''}
            {device.pathname ? ` · ${device.pathname}` : ''}
          </p>
          <p className="break-all font-mono text-[11px] text-muted-foreground">
            {device.device_id}
          </p>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        {ROLES.map((role) => (
          <Button
            key={role.value}
            variant={device.device_role === role.value ? 'default' : 'outline'}
            className="h-12"
            disabled={saving || device.device_role === role.value}
            onClick={() => onRole(role.value)}
          >
            {role.label}
          </Button>
        ))}
      </div>
    </li>
  );
}

export function DeviceRolesPanel() {
  const tenantId = useEffectiveTenantId();
  const queryClient = useQueryClient();
  const currentId = getOrCreateDeviceId();
  const { data: devices = [], isLoading } = useQuery({
    queryKey: ['tenant_devices', tenantId],
    enabled: !!tenantId,
    refetchOnMount: 'always',
    queryFn: async () => {
      const result = await listTenantDevices(tenantId ?? '');
      if (!result.ok) throw new Error(errMsg(result));
      return result.value;
    },
  });
  const change = useMutation({
    mutationFn: async ({ id, role }: { id: string; role: DeviceRole }) => {
      const result = await setDeviceRole(id, role);
      if (!result.ok) throw new Error(errMsg(result));
      return result.value;
    },
    onSuccess: (role) => {
      toast.success(
        role === 'pda_stock' ? 'Appareil passé en PDA stock' : 'Appli complète rétablie',
      );
      void queryClient.invalidateQueries({ queryKey: ['tenant_devices'] });
      void queryClient.invalidateQueries({ queryKey: ['device_role'] });
    },
    onError: (error: Error) => toast.error(error.message),
  });

  return (
    <div className="mx-auto w-full max-w-2xl space-y-4 p-3 md:p-6" data-testid="device-roles">
      <div>
        <h2 className="text-xl font-bold">Appareils</h2>
        <p className="text-sm text-muted-foreground">
          « PDA stock » : l'appareil démarre sur le Mode Stock et n'affiche que le stock (scan,
          réception, inventaire, étiquettes, mouvements). Caisse, paiement et TPE y sont
          inaccessibles. Les appareils apparaissent après leur première connexion.
        </p>
      </div>
      {isLoading && <Loader2 className="mx-auto h-6 w-6 animate-spin" />}
      <ul className="space-y-3">
        {devices.map((device) => (
          <DeviceCard
            key={device.id}
            device={device}
            current={device.device_id === currentId}
            saving={change.isPending}
            onRole={(role) => change.mutate({ id: device.id, role })}
          />
        ))}
      </ul>
    </div>
  );
}
